import React, { useEffect, useState } from 'react';
import { NextComponentType, NextPageContext } from 'next';
import { useRouter } from 'next/router';
import { PageProps } from 'types/page';
import useStyles from './styles';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { buildLoginRedirectUrl } from 'lib/redirects';
import { useAuth } from '@elevator/ui';

/**
 * HOC that ensures a user is authenticated before rendering the wrapped component.
 * Redirects to login page if user is not authenticated, shows loading state while checking,
 * and handles error states during authentication check.
 *
 * @param Component - The component to protect with authentication requirement
 * @returns Wrapped component that only renders if user is authenticated
 */
const withRequiresAuth = (
  Component: NextComponentType<NextPageContext, any, PageProps>,
) => {
  const WrappedComponent = (props: PageProps) => {
    const { currentUser, hydrateUser } = useAuth();
    const router = useRouter();
    const { classes } = useStyles();

    const [isChecking, setIsChecking] = useState(true);
    const [hasError, setHasError] = useState(false);

    // Check authentication status on mount and when currentUser changes
    useEffect(() => {
      let isMounted = true;

      const checkAuth = async () => {
        try {
          setIsChecking(true);
          setHasError(false);

          // If we already have a user, we're authenticated
          if (currentUser) {
            if (isMounted) {
              setIsChecking(false);
            }
            return;
          }

          // Try to hydrate user (force network request to ensure fresh auth state)
          const hydratedUser = await hydrateUser(false);

          if (isMounted) {
            setIsChecking(false);

            // If still no user after hydration, redirect to login
            if (!hydratedUser) {
              const returnPath = router.asPath;
              const loginUrl = buildLoginRedirectUrl(returnPath);

              // Use Next.js router for client-side navigation (no page reload)
              router.replace(loginUrl);
            }
          }
        } catch (error) {
          // Handle errors gracefully
          if (isMounted) {
            setHasError(true);
            setIsChecking(false);

            // Still redirect to login on error (better than showing protected content)
            const returnPath = router.asPath;
            const loginUrl = buildLoginRedirectUrl(returnPath);
            router.replace(loginUrl);
          }
        }
      };

      checkAuth();

      return () => {
        isMounted = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    // Show loading state while checking authentication
    if (isChecking) {
      return (
        <div className={classes.root}>
          <CircularProgress />
        </div>
      );
    }

    // Show error state (though we'll redirect anyway)
    if (hasError) {
      return (
        <div className={classes.root}>
          <CircularProgress />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Checking authentication...
          </Typography>
        </div>
      );
    }

    // If no user after checking, show loading (redirect is in progress)
    if (!currentUser) {
      return (
        <div className={classes.root}>
          <CircularProgress />
        </div>
      );
    }

    // User is authenticated, render the protected component
    return <Component {...props} />;
  };

  WrappedComponent.displayName = `withRequiresAuth(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};

export default withRequiresAuth;
